import type { IArticleConfig } from '~/config/article';
import ModalWindow from '~/modules/iframes/ModalWindow';
import NotificationAlert from '~/modules/NotificationAlert';
import type { ILoggerAction } from '~/modules/ServerLogger';
import ServerLogger from '~/modules/ServerLogger';
import Session from '~/modules/Session';
import TokenHandler from '~/modules/TokenHandler';
import type { AudioWidgetListener } from './AudioWidgetListener';
import type { PowerbarListener } from './PowerbarListener';
import type { QuizListener } from './QuizListener';
import { debugLog } from '~/debug';
import SecureFormHandler from './Helpers/SecureFormHandler';

/**
 * Windowed modal communication listener
 */
export class ModalWindowListener {
  protected widgetObject: ModalWindow;
  private userFormDataHandler = new SecureFormHandler(this.articleConfig.apiKey);

  constructor(
    widgetObject: ModalWindow,
    private readonly articleConfig: IArticleConfig,
    private readonly widgetListener: PowerbarListener | AudioWidgetListener | QuizListener | undefined
  ) {
    this.widgetObject = widgetObject;
    window.addEventListener('message', this.listener);
    this.postMessage({ canUseCookies: Session.canUseCookies });
    this.postMessage({ loginToken: TokenHandler.getToken() });
    this.postMessage({ isMobile: Session.isMobileUser });
    this.postMessage({
      customShareUrls:
        (articleConfig.powerbar && articleConfig.powerbar.customUrls) ||
        (articleConfig.sharebar && articleConfig.sharebar.customUrls),
    });
    this.postMessage({ userCountry: Session.userCountry });
  }

  /**
   * Modal widget messages listener
   * NOTE: We need to use arrow function here to bind function to this object instead of the event.
   * @param {any} event - message event
   */
  protected listener = (event: any) => {
    if (!this.isValidEvent(event)) {
      return;
    }

    debugLog('log', '[Vuukle.js | Modal(Windowed)] message:', event);

    // We can receive also event from modal to close it
    if (event.data.modalReply?.hideModal) {
      this.handleHideModal();
    }

    // Send submitted form to the opened widget
    if (event.data.modalReply?.userData) {
      this.userFormDataHandler.processFormData(event.data.modalReply.userData);
      this.widgetListener?.postMessage({ userData: event.data.modalReply.userData });
    }

    // modal loaded
    if (event.data.modalLoaded) {
      this.widgetListener?.postMessage({ modalLoaded: event.data.modalLoaded });
    }

    if (event.data.modalMessage) {
      this.broadcastModalMessage(event);
    }

    if (event.data.openLinkInNewTab) {
      const anchorTag = document.createElement('a');
      anchorTag.href = event.data.openLinkInNewTab;
      anchorTag.target = '_blank';
      anchorTag.rel = 'nofollow noopener';
      anchorTag.click();
    }

    if (event.data.openLinkInSameTab) {
      window.location.href = event.data.openLinkInSameTab;
    }

    // 📊 BQ events reporting
    if (event.data.reportEvent) {
      this.reportToAnalyticsServer(event);
    }

    if (event.data.callSSO) {
      /** SSO methods */
      this.callSSOAuth();
    }

    /** Cookies are enabled when user does a successful regular login and disabled if they have don't consent */
    // if (typeof event.data.enableCookies === 'boolean') {
    //   this.changeCookieSettingsForWidgets(event.data.enableCookies);
    // }

    /** If modal is attempting to open a share network window, tell powerbar to do it */
    if (event.data.openedShareNetwork) {
      if (this.widgetListener) {
        this.widgetListener.postMessage(
          { openedShareNetwork: event.data.openedShareNetwork },
          { sendToOneInstance: true }
        );
      }
    }

    // create an alert notification
    if (event.data.alertMessage) {
      NotificationAlert.createNotification(event.data.alertMessage.text, event.data.alertMessage.type);
    }

    /** Execute all postMessage callbacks when modal window loads */
    if (event.data.modalWindowLoaded) {
      this.widgetObject.executeAllCallbacks();
    }

    // Share event with publisher if needed
    if (event.data.vuukle_event) {
      this.shareEventToPublished(event.data.vuukle_event, this.articleConfig.eventHandler);
    }

    if (event.data.signUpComplete) {
      ServerLogger.log('user_register', this.articleConfig);
    }

    if (event.data.loginToken && event.data.tokenType && event.data.loggedInFrom) {
      if (event.data.tokenType === 'regular') {
        TokenHandler.setRegularCommenting(true);
        TokenHandler.setGuestCommenting(false);
        TokenHandler.setAnonymousCommenting(false);
      } else if (event.data.tokenType === 'guest') {
        TokenHandler.setRegularCommenting(false);
        TokenHandler.setGuestCommenting(true);
        TokenHandler.setAnonymousCommenting(false);
      }

      if (event.data.loggedInFrom === 'quiz') {
        ServerLogger.log('login_qw', this.articleConfig);
      } else if (event.data.loggedInFrom === 'comments') {
        ServerLogger.log('login_cw', this.articleConfig);
      }

      TokenHandler.saveToken(event.data.loginToken);
      this.widgetListener?.postMessage({ loginToken: event.data.loginToken });
    }
  };

  private broadcastModalMessage(event: any) {
    /** Notify of successful login action */
    if (event.data.modalMessage.loggedIn) {
      this.widgetListener?.postMessage({ loggedIn: true });
    } else {
      this.widgetListener?.postMessage({ modalMessage: event.data.modalMessage });
    }
  }

  private reportToAnalyticsServer(event: any) {
    const { action } = event.data.reportEvent;

    // Log action to server for analytics
    if (typeof action === 'string') {
      ServerLogger.log(action as ILoggerAction, this.articleConfig);
    }
  }

  private callSSOAuth() {
    const ssoAuth = this.articleConfig.comments?.auth?.sso?.onClick || this.articleConfig.auth?.sso?.onClick;
    /** Call auth or show warning that it's not configured */
    typeof ssoAuth === 'function'
      ? ssoAuth()
      : console.error(`[Vuukle] ⚠ Please configure SSO to allow users to authenticate.`);
  }

  private handleHideModal() {
    this.widgetObject.closeWindow();
  }

  /**
   * 🔧 Simple util to verify event
   * @param {any} event - Message event to verify
   * @returns {boolean}
   */
  protected isValidEvent(event: any): boolean {
    // Ensure the event structure is correct
    if (!(event instanceof Object) && !(event.data instanceof Object) && !event.data.sourceWindowName) {
      return false;
    }

    // The event must be from the modal window itself
    if (event.data.sourceWindowName !== this.widgetObject.windowName) {
      return false;
    }

    return true;
  }

  /**
   * Share event with publisher if configured
   * @param {any} event - event to share with eventHandler
   * @param {any} eventHandler - possible event handler function that publisher added to the article config
   * @returns {void}
   */
  private shareEventToPublished(event: any, eventHandler: any): void {
    if (typeof eventHandler === 'function') {
      eventHandler(event);
    }
  }

  /**
   * Send theme color to Modal
   * @param color
   */
  public changeThemeColor(color: string) {
    this.postMessage({ themeColor: color });
  }

  /**
   * Post message to the iframe
   * @param data
   */
  public postMessage(data: any): void {
    this.widgetObject.onLoad((modalWindow) => modalWindow.postMessage(data, '*'));
  }
}
